import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import {
  CaretDownIcon,
  CaretRightIcon,
  PlusIcon,
} from "phosphor-react-native";
import React from "react";
import { Pressable, View } from "react-native";

type TreeNodeProps = {
  /** Indentation depth (0 = root level) */
  depth: number;
  /** Whether this node is expanded */
  expanded: boolean;
  /** Whether this node has children */
  hasChildren: boolean;
  /** If true, no divider line is shown under this row (letztes Element im Baum) */
  isLastInTree?: boolean;
  /** Called when expand/collapse toggle is pressed */
  onToggleExpand: () => void;
  /** Called when the node row is pressed */
  onPress: () => void;
  /** Called when the add child button is pressed */
  onAddChild?: () => void;
  /** Optional trailing action (e.g. delete icon) – rendered after add button */
  trailing?: React.ReactNode;
  /** Content to render inside the node */
  children: React.ReactNode;
};

export function TreeNode({
  depth,
  expanded,
  hasChildren,
  isLastInTree = false,
  onToggleExpand,
  onPress,
  onAddChild,
  trailing,
  children,
}: TreeNodeProps) {
  const { theme } = useTheme();
  const s = useStyles();

  return (
    <View style={{ marginLeft: depth * theme.space.lg }}>
      <View
        style={[
          s.row,
          {
            backgroundColor: theme.color.surface,
            marginBottom: theme.space.xs,
            ...(isLastInTree ? {} : { borderBottomWidth: 1, borderBottomColor: theme.color.border }),
          },
        ]}
      >
        {/* Expand/Collapse toggle */}
        <Pressable
          onPress={onToggleExpand}
          style={[s.center, { padding: theme.space.sm, width: 40 }]}
        >
          {hasChildren ? (
            expanded ? (
              <CaretDownIcon size={18} color={theme.color.muted} />
            ) : (
              <CaretRightIcon size={18} color={theme.color.muted} />
            )
          ) : (
            <View style={{ width: 18 }} />
          )}
        </Pressable>

        {/* Main content area */}
        <Pressable
          onPress={onPress}
          style={{
            flex: 1,
            paddingVertical: theme.space.sm,
            paddingRight: theme.space.sm,
          }}
        >
          {children}
        </Pressable>

        {/* Add child button */}
        {onAddChild && (
          <Pressable
            onPress={onAddChild}
            style={[s.iconBgSmall, { marginRight: theme.space.xs }]}
          >
            <PlusIcon size={16} color={theme.color.muted} weight="bold" />
          </Pressable>
        )}
        {/* Optional trailing (e.g. delete) */}
        {trailing != null ? (
          <View style={{ marginRight: theme.space.sm }}>{trailing}</View>
        ) : null}
      </View>
    </View>
  );
}
